/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Modal } from "react-bootstrap";
import BaseCom from "../../BaseCom";
import FormInput from "../../FormInput";
import Icon from "../../Icon";
import { qreq } from "../../../shared/qrequest";
import { v4 as uuidv4 } from 'uuid';

export default class FormBuilder extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            form: {
                views: []
            },
            fieldTypes: []
        };
    }

    async componentDidMount() { 
        await this.fillListAsync('/api/form/fieldtype/list', 'fieldTypes');
    }

    componentDidUpdate(prevProps) {
        if(this.props.initialProps !== prevProps.initialProps) {
            this.setState({ form: {...this.props.initialProps}});
        }
    }

    addField = (view) => {
        let fields = view.fields || [];
        fields.push({
            name: null,
            edit: true
        });
        view.fields = fields;
        this.setState({ form: { ...this.state.form } });
    }

    addView = () => {
        let views = [...this.state.form.views];
        views.push({
            name: null,
            edit: true,
            fields: []
        });
        this.setState({ form: { ...this.state.form, views: views } });
    }

    removeView = (view) => {
        let views = this.state.form.views || [];
        views = views.filter(v => v !== view);
        this.setState({ form: { ...this.state.form, views: views } });
    }

    removeField = (view, field) => {
        let fields = view.fields || [];
        fields = fields.filter(f => f !== field);
        view.fields = fields;
        this.setState({ form: { ...this.state.form } });
    }

    addOption = (view, field) => {
        let options = field.options || [];
        options.push({
            name: null,
            edit: true
        });
        field.options = options;
        this.setState({ form: { ...this.state.form } });
    }

    removeOption = (view, field, option) => {
        let options = field.options || [];
        options = options.filter(o => o !== option);
        field.options = options;
        this.setState({ form: { ...this.state.form } });
    }

    isSelector = (fieldType) => {
        var ft = this.state.fieldTypes.find(f => f.id === parseInt(fieldType));
        return ft && ft.isSelector;
    }

    submit = (e) => {
        e?.preventDefault();
        this.props.col.extendedProps = {...this.state.form}; // extendedProps is a custom property that can be used to store additional data
        this.props.onSubmit();
    }

    moveField = (i,f,fdx,shiftIdx) => {
        i.fields.splice(fdx, 1);
        i.fields.splice(fdx + shiftIdx, 0, f);
        this.setState({ form: { ...this.state.form } });
    }

    moveOption = (f,o,odx,shiftIdx) => {
        f.options.splice(odx, 1);
        f.options.splice(odx + shiftIdx, 0, o);
        this.setState({ form: { ...this.state.form } });
    }


    render() {
        return (
            <Modal show={this.props.show} onHide={this.submit} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Form Builder</Modal.Title>
                </Modal.Header>
                <form onSubmit={this.submit}>
                    <Modal.Body>
                        <FormInput type="text" label="Form Name" name="name" model={this.state.form} required />
                        {this.state.form?.views?.map((i, idx) => (
                            <div className="bg-light p-3 mb-3 rounded" key={idx}>
                                <div className="row mb-3">
                                    <div className="col-10">
                                        {i.edit || !i.name
                                            ? <FormInput type="text" label="View Name" name="name" model={i} labelAsPlaceholder required />
                                            : i.name}
                                    </div>
                                    <div className="col-2 text-end">
                                        <div className="btn-group">
                                            {i.edit || !i.name
                                                ? <button type="button" className="btn btn-primary" onClick={() => i.edit = false}>
                                                    <Icon icon="faCheck" />
                                                </button>
                                                : <button type="button" className="btn btn-primary" onClick={() => i.edit = true}>
                                                    <Icon icon="faEdit" />
                                                </button>}
                                            <button type="button" className="btn btn-danger" onClick={() => this.removeView(i)}>
                                                <Icon icon="faTrash" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {i.fields?.map((f, fdx) => (
                                    <div key={fdx} className="mb-3">
                                    <div className="d-flex justify-content-start">
                                        <div>
                                            <button type="button" className="btn btn-secondary me-1" onClick={() => this.moveField(i,f,fdx,-1)} disabled={fdx === 0}><Icon icon="faArrowUp" /></button>
                                            <button type="button" className="btn btn-secondary me-1" onClick={() => this.moveField(i,f,fdx,1)} disabled={fdx === i.fields.length - 1}><Icon icon="faArrowDown" /></button>
                                        </div>
                                        <div className="flex-grow-1">
                                            <FormInput
                                                model={f}
                                                type="text"
                                                name="name"
                                                label="Field Name"
                                                labelAsPlaceholder
                                                required
                                            />
                                        </div>
                                        <div>
                                            <FormInput
                                                model={f}
                                                type="select"
                                                name="fieldType"
                                                label="Field Type"
                                                labelAsPlaceholder
                                                options={this.state.fieldTypes}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <button type="button" className="btn btn-danger" onClick={() => this.removeField(i, f)}>
                                                <Icon icon="faTrash" />
                                            </button>
                                        </div>
                                    </div>
                                    {this.isSelector(f.fieldType) && (
                                        <div className="bg-secondary text-light p-3 rounded">
                                            {f.options?.map((o, odx) => (
                                                <div key={odx} className="d-flex justify-content-start">
                                                    <div>
                                                        <button type="button" className="btn btn-secondary me-1" onClick={() => this.moveOption(f,o,odx,-1)} disabled={odx === 0}><Icon icon="faArrowUp" /></button>
                                                        <button type="button" className="btn btn-secondary me-1" onClick={() => this.moveOption(f,o,odx,1)} disabled={odx === f.options.length - 1}><Icon icon="faArrowDown" /></button>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <FormInput
                                                            model={o}
                                                            type="text"
                                                            name="name"
                                                            label="Option Name"
                                                            labelAsPlaceholder
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <button type="button" className="btn btn-danger" onClick={() => this.removeOption(i, f, o)}>
                                                            <Icon icon="faTrash" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}

                                            <div className="text-start">
                                                <button type="button" className="btn btn-primary" onClick={() => this.addOption(i,f)}>
                                                    Add Option
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    </div>
                                ))}

                                <div className="text-end">
                                    <button type="button" className="btn btn-primary" onClick={() => this.addField(i)}>Add Field</button>
                                </div>
                            </div>

                        ))}
                        <div>
                            <button type="button" className="btn btn-primary" onClick={this.addView}>Add View</button>
                        </div>
                    </Modal.Body>
                </form>
            </Modal>
        );
    }
}